import { useEffect, useState, Fragment, useMemo, useRef } from 'react';

import DateFnsUtils from '@date-io/date-fns';
import { IconButton, LinearProgress } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers';
import Axios from 'axios';
import ptLocale from 'date-fns/locale/pt-BR';
import moment from 'moment/moment';
import { connect } from 'react-redux';

import CommonAlert from 'components/CommonAlert/CommonAlert';
import TitleHead from 'components/TitleHead';
import Layout from 'containers/layouts/layout';
import WelcomeBanner from 'pages/home/labelHome/components/WelcomeBanner';
import { Container, GlobalStyle } from 'styles/general';
import Environment from 'utils/environments';

const STATUS_COLOR = {
  NO_USAGE: '#9296AC',
  NO_EXPIRED_LABELS: '#08AA15',
  EXPIRED_LABELS: '#F53D4C'
};

const getCircleColor = (status) =>
  STATUS_COLOR[status] || STATUS_COLOR.NO_USAGE;

const DAY_STATUS_COLOR = {
  NO_USAGE: '#D0D3E2',
  NO_EXPIRED_LABELS: '#8CF295',
  EXPIRED_LABELS: '#F76E79'
};
const getDayCircleColor = (status) =>
  DAY_STATUS_COLOR[status] || DAY_STATUS_COLOR.NO_USAGE;

const dateKey = (d) => moment(d).format('DD/MM');
const normalizeToKey = (d) => dateKey(new Date(d));

const statusesAligned = (dateColumns, map) =>
  dateColumns.map((d) => map[d] ?? 'NO_USAGE');

const buildMainList = (apiContent, dateColumns) => {
  return (apiContent || []).map((sg, idx) => {
    const sgStatusMap = {};
    (sg.subGroupStatus || []).forEach(({ date, status }) => {
      sgStatusMap[normalizeToKey(date)] = status;
    });

    const items = (sg.stores || []).map((st) => {
      const storeStatusMap = {};
      (st.days || []).forEach(({ date, status }) => {
        storeStatusMap[normalizeToKey(date)] = status;
      });

      return {
        name: st.description,
        status: statusesAligned(dateColumns, storeStatusMap),
        aggregateStatus: st.status
      };
    });

    return {
      name: sg.description,
      id: `group-${idx + 1}`,
      statusByDate: sgStatusMap,
      items
    };
  });
};

function LabelAdminHome({ userInfo }) {
  const environments = Environment(process.env.REACT_APP_ENV);
  const userLocal = JSON.parse(localStorage.getItem('companies'));
  const profile = userInfo?.profile[0][0]?.name;
  const isLabelAdmin = profile === 'admin_label';
  const activeRestaurantId = isLabelAdmin
    ? userLocal?.labelId
    : userInfo.companiesActive.id;

  const [startDate, setStartDate] = useState(
    moment().subtract(14, 'days').toDate()
  );
  const [endDate, setEndDate] = useState(moment().subtract(1, 'day').toDate());

  const [filtersChanged, setFiltersChanged] = useState([]);

  const dateColumns = useMemo(() => {
    if (!moment(startDate).isValid() || !moment(endDate).isValid()) return [];
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).startOf('day');
    const diff = Math.max(end.diff(start, 'days'), 0);
    return Array.from({ length: diff + 1 }, (_, i) =>
      start.clone().add(i, 'days').format('DD/MM')
    );
  }, [startDate, endDate]);

  const [openGroups, setOpenGroups] = useState({});

  const [callSettings, setCallSettings] = useState({
    page: 1,
    totalPages: 1,
    size: 100,
    mainList: [],
    numberOfElements: 0,
    loading: true,
    totalElements: 0
  });
  const { mainList, loading } = callSettings;

  const [message, setMessage] = useState({
    description: '',
    status: ''
  });

  const config = {
    headers: {
      User: `${userLocal?.user}`
    }
  };

  const wrapperRef = useRef(null);
  const tableRef = useRef(null);
  const legendRef = useRef(null);

  const updateLegendOffset = useMemo(() => {
    let ro;
    return () => {
      const wrapper = wrapperRef.current;
      const table = tableRef.current;
      const legend = legendRef.current;
      if (!wrapper || !table || !legend) return;

      const wrapperWidth = wrapper.clientWidth;
      const tableWidth = table.offsetWidth;
      const delta = Math.max(0, wrapperWidth - tableWidth);

      legend.style.setProperty('--legend-offset', `${delta}px`);
    };
  }, []);

  const toggleGroup = (group) => {
    setOpenGroups((prev) => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  const hasNoDataForAllDates = (item) => {
    const relevantes = item?.status || [];
    if (relevantes.length === 0) return false;
    return relevantes.every((st) => st === 'NO_USAGE');
  };

  const fetchLabelStatus = async (dateCols) => {
    try {
      setCallSettings((s) => ({ ...s, loading: true }));

      const startAt = moment(startDate).format('YYYY-MM-DD');
      const endAt = moment(endDate).format('YYYY-MM-DD');

      const groupId = userLocal?.companies?.groups?.[0]?.id;

      const { data } = await Axios.get(
        `${environments.dashBoard}/labelStatus`,
        { params: { groupId, startAt, endAt }, ...config }
      );

      if (!data?.success) {
        throw new Error(data?.message || 'Falha na consulta');
      }

      const mainListBuilt = buildMainList(data.content || [], dateCols);

      setCallSettings((s) => ({
        ...s,
        loading: false,
        mainList: mainListBuilt,
        totalPages: 1,
        numberOfElements: mainListBuilt.length,
        totalElements: mainListBuilt.length
      }));
    } catch (err) {
      console.error(err);
      setMessage({
        description: `Erro ao carregar status`,
        status: 'error',
        autoHideDuration: 5000
      });
      setCallSettings((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => {
    if (mainList?.length) {
      const initialOpenGroups = Object.fromEntries(
        mainList.map((group) => [group.name, true])
      );
      setOpenGroups(initialOpenGroups);
    }
  }, [mainList]);

  useEffect(() => {
    setCallSettings((s) => ({ ...s, page: 1 }));
    if (dateColumns.length) {
      fetchLabelStatus(dateColumns);
    }
  }, [activeRestaurantId, filtersChanged, startDate, endDate, dateColumns]);

  useEffect(() => {
    setCallSettings((s) => ({
      ...s,
      activeRestaurantId
    }));
  }, [userInfo, activeRestaurantId]);

  useEffect(() => {
    updateLegendOffset();

    const wrapper = wrapperRef.current;
    const table = tableRef.current;

    if (!wrapper || !table) return;

    const ro = new ResizeObserver(() => updateLegendOffset());
    ro.observe(wrapper);
    ro.observe(table);

    const onResize = () => updateLegendOffset();
    window.addEventListener('resize', onResize);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [updateLegendOffset, dateColumns.length, loading]);

  const DEFAULT_COLS = 14; 
  const isDefaultRange = dateColumns.length === DEFAULT_COLS;

  return (
    <Layout>
      <TitleHead title="Home" />

      <GlobalStyle />

      <Container className="adminLabelHome">
        <WelcomeBanner />
        <div className="tableInner">
          <div className="tableWrapper" ref={wrapperRef}>
            <table
              ref={tableRef}
              className={`desktop ${isDefaultRange ? 'isDefault' : ''}`}
              cellPadding="0"
              cellSpacing="0"
              style={{
                '--n': dateColumns.length,
                '--first-col': '500px'
              }}
            >
              <thead>
                <tr className="headerTitle">
                  <th className="pr-0 pl-0 filterColumn">
                    <div className="dFlex alignCenter borderR">
                      <span className="mr12">Data</span>

                      <MuiPickersUtilsProvider
                        utils={DateFnsUtils}
                        locale={ptLocale}
                      >
                        <div
                          className="dFlex alignCenter pb-1"
                          style={{ gap: 8 }}
                        >
                          <KeyboardDatePicker
                            size="small"
                            id="time-picker"
                            label="Data Início"
                            ampm={false}
                            clearable
                            format="dd/MM"
                            value={startDate}
                            inputVariant="outlined"
                            className="dateSmall"
                            autoOk
                            InputLabelProps={{
                              shrink: true,
                              style: { fontSize: 12 }
                            }}
                            InputProps={{
                              style: { fontFamily: 'Lato', fontSize: '12px' }
                            }}
                            onChange={(date) => {
                              if (!date) return;
                              if (moment(date).isSameOrAfter(endDate, 'day')) {
                                setEndDate(moment(date).add(1, 'day').toDate());
                              }
                              setStartDate(moment(date).toDate());
                            }}
                            InputAdornmentProps={{ position: 'start' }}
                            KeyboardButtonProps={{
                              'aria-label': 'change time'
                            }}
                          />

                          <span>à</span>

                          <KeyboardDatePicker
                            size="small"
                            id="time-picker"
                            label="Data Fim"
                            ampm={false}
                            clearable
                            format="dd/MM"
                            value={endDate}
                            inputVariant="outlined"
                            className="dateSmall"
                            autoOk
                            InputProps={{
                              style: { fontFamily: 'Lato', fontSize: '12px' }
                            }}
                            onChange={(date) => {
                              if (!date) return;
                              if (
                                moment(date).isSameOrBefore(startDate, 'day')
                              ) {
                                setStartDate(
                                  moment(date).subtract(1, 'day').toDate()
                                );
                              }
                              setEndDate(moment(date).toDate());
                            }}
                            InputAdornmentProps={{ position: 'start' }}
                            KeyboardButtonProps={{
                              'aria-label': 'change time'
                            }}
                          />
                        </div>
                      </MuiPickersUtilsProvider>
                    </div>
                  </th>

                  {dateColumns.map((date, index) => {
                    const isFirstElement = index === 0;
                    const isLastElement = index === dateColumns.length - 1;

                    return (
                      <th
                        key={date}
                        className={`subtitle dateColumn ${
                          isFirstElement ? 'firstColumn' : ''
                        } ${isLastElement ? 'lastColumn' : ''}`}
                      >
                        {date}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              {loading && (
                <tbody>
                  <tr>
                    <th
                      colSpan={dateColumns.length + 1}
                      className="m10 textAlignCenter"
                    >
                      <LinearProgress
                        style={{ height: '7px' }}
                        id="loading"
                        variant="query"
                      />
                    </th>
                  </tr>
                </tbody>
              )}

              {!loading && (
                <tbody>
                  {mainList?.map((group) => (
                    <Fragment key={group?.id}>
                      <tr className="groupLine">
                        <td className="groupNameCell">
                          <div className="borderR">
                            <strong className="pl12">{group?.name}</strong>
                            <IconButton
                              size="small"
                              onClick={() => toggleGroup(group?.name)}
                            >
                              {openGroups[group?.name] ? (
                                <ExpandLess />
                              ) : (
                                <ExpandMore />
                              )}
                            </IconButton>
                          </div>
                        </td>

                        {dateColumns.map((date, index) => {
                          const isFirstElement = index === 0;
                          const isLastElement =
                            index === dateColumns.length - 1;

                          const status =
                            group.statusByDate?.[date] || 'NO_USAGE';
                          const color = getCircleColor(status);

                          return (
                            <td
                              key={`${group?.id}-${date}-${index}`}
                              align="center"
                              className={`statusCell ${
                                isFirstElement ? 'firstColumn' : ''
                              } ${isLastElement ? 'lastColumn' : ''}`}
                            >
                              <div className="dFlex alignCenter justifyCenter">
                                <span
                                  className="circle"
                                  style={{ backgroundColor: color }}
                                />
                              </div>
                            </td>
                          );
                        })}
                      </tr>

                      {openGroups[group?.name] &&
                        group?.items?.map((item) => (
                          <Fragment key={item?.name}>
                            <tr>
                              <td className="pl12">
                                <div className="dFlex alignCenter borderR">
                                  <span
                                    className="circle mr12"
                                    style={{
                                      backgroundColor: getCircleColor(
                                        item.aggregateStatus
                                      )
                                    }}
                                  />
                                  <span
                                    className={`storeName ${
                                      hasNoDataForAllDates(item) ? 'noData' : ''
                                    }`}
                                  >
                                    {item?.name}
                                  </span>
                                </div>
                              </td>

                              {dateColumns.map((date, idx) => {
                                const isFirstElement = idx === 0;
                                const isLastElement =
                                  idx === dateColumns.length - 1;

                                const st = item?.status?.[idx] || 'NO_USAGE';
                                const color = getDayCircleColor(st);

                                return (
                                  <td
                                    key={`${item?.name}-${idx}`}
                                    align="center"
                                    className={`statusCell ${
                                      isFirstElement ? 'firstColumn' : ''
                                    } ${isLastElement ? 'lastColumn' : ''}`}
                                  >
                                    <div className="dFlex alignCenter justifyCenter">
                                      <span
                                        className="circle"
                                        style={{
                                          backgroundColor: color,
                                          opacity: 0.7
                                        }}
                                      />
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>

                            <tr>
                              <td colSpan={dateColumns.length + 1}>
                                <hr />
                              </td>
                            </tr>
                          </Fragment>
                        ))}

                      <tr>
                        <td colSpan={dateColumns.length + 1}>
                          <hr />
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              )}
            </table>
          </div>
          <div className="legend" ref={legendRef}>
            <div className="legendItem">
              <span
                className="circle"
                style={{
                  backgroundColor: DAY_STATUS_COLOR.NO_EXPIRED_LABELS
                }}
              />
              <span>Sem etiquetas vencidas</span>
            </div>

            <div className="legendItem">
              <span
                className="circle"
                style={{ backgroundColor: DAY_STATUS_COLOR.EXPIRED_LABELS }}
              />
              <span>Com etiquetas vencidas</span>
            </div>

            <div className="legendItem">
              <span
                className="circle"
                style={{ backgroundColor: DAY_STATUS_COLOR.NO_USAGE }}
              />
              <span>Sem utilização</span>
            </div>
          </div>
        </div>

        <CommonAlert
          open={!!message?.description}
          severity={message?.status}
          onClose={() => {
            setMessage({
              description: '',
              status: ''
            });
          }}
          messagePersonal={message?.description}
          autoHideDuration={2000}
        />
      </Container>
    </Layout>
  );
}

const mapStateToProps = (state) => ({
  userInfo: state.user
});

export default connect(mapStateToProps)(LabelAdminHome);
